/**
 * Barras agrupadas por mes. Tres series como mucho.
 *
 * Con más de tres, las barras de un mismo mes bajan de cuatro píxeles y el
 * gráfico deja de poder compararse: la cuarta serie se pliega en «Otros» o se
 * saca a su propio gráfico. No es una limitación técnica, es dónde deja de
 * leerse.
 */

import { useId, useState } from "react";
import { formatMillions, formatMonth } from "../format";
import { Leyenda, type Serie } from "./Leyenda";
import { useAncho } from "./medida";
import { type Cifra, cifras, escala, extremos, marcas } from "./escala";

export interface SerieMensual extends Serie {
  /** Crudo de la API: puede venir como cadena. Se convierte al entrar. */
  valores: Cifra[];
}

const MARGEN = { arriba: 10, derecha: 12, abajo: 22, izquierda: 54 };

export function BarrasMes({
  meses,
  series,
  alto = 200,
}: {
  meses: string[];
  series: SerieMensual[];
  alto?: number;
}) {
  const [activo, setActivo] = useState<number | null>(null);
  const id = useId();
  // El `viewBox` toma el ancho REAL de la caja para que la escala sea 1:1.
  const [caja, ancho] = useAncho();

  const limpias = series.map((s) => ({ ...s, valores: cifras(s.valores) }));
  const todos = limpias.flatMap((s) => s.valores).filter((v): v is number => v !== null);
  if (!meses.length || !todos.length) return null;

  const dominio = extremos(todos);
  const y = escala(dominio, [alto - MARGEN.abajo, MARGEN.arriba]);
  const banda = (ancho - MARGEN.izquierda - MARGEN.derecha) / meses.length;
  // 2px de separación entre barras contiguas: sin el hueco, dos rellenos
  // adyacentes se leen como una sola barra de dos tonos.
  const grosor = Math.max(2, banda / limpias.length - 2);
  const cero = y(0);

  /** Lo que el tooltip enseña al puntero, dicho para quien no lo ve. */
  const rotulo = (i: number) =>
    `${formatMonth(meses[i])}: ` +
    limpias.map((s) => `${s.label} ${formatMillions(s.valores[i])}`).join("; ");

  return (
    <div className="viz" ref={caja}>
      <Leyenda series={limpias} />
      <svg
        className="viz-lienzo"
        viewBox={`0 0 ${ancho} ${alto}`}
        preserveAspectRatio="none"
        role="img"
        aria-labelledby={id}
        onMouseLeave={() => setActivo(null)}
      >
        <title id={id}>{limpias.map((s) => s.label).join(", ")} por mes</title>
        {marcas(dominio).map((v) => (
          <g key={v}>
            <line
              className="viz-rejilla"
              x1={MARGEN.izquierda}
              x2={ancho - MARGEN.derecha}
              y1={y(v)}
              y2={y(v)}
            />
            <text className="viz-rotulo-eje" x={MARGEN.izquierda - 8} y={y(v) + 3} textAnchor="end">
              {formatMillions(v)}
            </text>
          </g>
        ))}
        {meses.map((mes, i) => {
          const x0 = MARGEN.izquierda + i * banda;
          return (
            <g key={mes}>
              {/* La zona es enfocable y responde al foco además de al ratón:
                  un `<rect>` no recibe foco por sí solo, así que con solo
                  `onMouseEnter` los importes mensuales no existían para quien
                  navega con teclado. El `aria-label` los dice completos, que es
                  lo que el tooltip enseña al puntero. */}
              <rect
                className="viz-zona"
                x={x0}
                y={0}
                width={banda}
                height={alto}
                tabIndex={0}
                role="img"
                aria-label={rotulo(i)}
                onMouseEnter={() => setActivo(i)}
                onFocus={() => setActivo(i)}
                onBlur={() => setActivo(null)}
              />
              {limpias.map((s, j) => {
                const v = s.valores[i];
                if (v === null || !Number.isFinite(v)) return null;
                const yv = y(v);
                return (
                  <rect
                    key={s.id}
                    className={`viz-barra-mes serie-${s.slot}`}
                    x={x0 + 1 + j * (grosor + 2)}
                    y={Math.min(yv, cero)}
                    width={grosor}
                    height={Math.max(1, Math.abs(cero - yv))}
                    rx="1.5"
                  />
                );
              })}
              {(i === 0 || i === meses.length - 1 || meses.length <= 8) && (
                <text
                  className="viz-rotulo-eje"
                  x={x0 + banda / 2}
                  y={alto - 6}
                  textAnchor="middle"
                >
                  {formatMonth(mes)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {activo !== null && (
        <div className="viz-tooltip" role="status">
          <strong>{formatMonth(meses[activo])}</strong>
          {limpias.map((s) => (
            <div key={s.id}>
              <span className={`viz-marca serie-${s.slot}`} aria-hidden="true" />
              {s.label}
              <span className="num">{formatMillions(s.valores[activo])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
